import { forwardRef } from 'react';
import { Box, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import moment from 'moment-timezone';
import { getStyles } from './styles';
import { STANDARD_TIMESTAMP_FORMAT_NO_OFFSET } from '../../constants/date';

const buildTime = (ts, tz, format = STANDARD_TIMESTAMP_FORMAT_NO_OFFSET) => {
  const time = moment.tz(ts, tz);
  return {
    text: time.format(format),
    zone: time.format('z'),
    offset: time.utcOffset(),
  };
};

const renderTime = ({ text, zone }, boldOffset = true) => (
  <>
    {`${text} `}
    <Box component="span" sx={{ fontWeight: boldOffset ? 'bold' : 'inherit' }}>{zone}</Box>
  </>
);

const TimeWithTimezone = forwardRef(function TimeWithTimezone(props, ref) {
  const {
    timeTs,
    timezone,
    label,
    labelSx,
    valueSx,
    variant = 'default', // 'default' | 'inline' | 'tooltip'
    renderTooltip, // optional (localTime) => node for the tooltip variant, e.g. (localTime) => <>Local: {renderTime(localTime)}</>
    boldOffset = true,
    textFormat = STANDARD_TIMESTAMP_FORMAT_NO_OFFSET, // format of the visible text only; the tooltip always shows the full date time
    sx,
    ...rest
  } = props;

  const theme = useTheme();
  const isValidTime = !!timeTs && moment(timeTs).isValid();
  const styles = getStyles(theme, { variant });

  const renderValue = () => {
    if (!isValidTime) return <Box component="span" sx={[styles.time, valueSx]}>-</Box>;

    const localTime = buildTime(timeTs, moment.tz.guess(), textFormat);
    const localTooltipTime = buildTime(timeTs, moment.tz.guess());
    const targetTime = timezone ? buildTime(timeTs, timezone, textFormat) : null;
    const hasDifferentOffset = !!targetTime && targetTime.offset !== localTime.offset;
    const isTooltip = variant === 'tooltip';

    const getTooltipContent = () => {
      if (!isTooltip || !hasDifferentOffset) return '';
      if (renderTooltip) return renderTooltip(localTooltipTime);
      return `${localTooltipTime.text} ${localTooltipTime.zone}`;
    }  

    return (
      <>
        <Tooltip title={getTooltipContent()} placement="top" arrow>
          <Box component="span" sx={[styles.time, valueSx]}>
            {renderTime(targetTime || localTime, boldOffset)}
          </Box>
        </Tooltip>
        {!isTooltip && hasDifferentOffset && (
          <Box component="span" sx={[styles.time, valueSx]}>
            {renderTime(localTime, boldOffset)}
          </Box>
        )}
      </>
    );
  };

  return (
    <Box component="span" ref={ref} {...rest} sx={[styles.container, sx]}>
      {label && <Box component="span" sx={[styles.label, labelSx]}>{label}</Box>}
      <Box component="span" sx={styles.values}>{renderValue()}</Box>
    </Box>
  );
});

export default TimeWithTimezone;
